import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, ArrowRight, Plane, Home, Utensils, CalendarDays, Send, Loader } from 'lucide-react';

interface Service {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  price: string;
}

const services: Service[] = [
  {
    id: 'travel',
    name: 'Travel & Transport',
    description: 'Private jets, luxury car services, and VIP airport experiences',
    icon: <Plane className="w-6 h-6" />,
    features: ['Private Aviation', 'Chauffeur Service', 'VIP Airport Access', '24/7 Support'],
    price: 'From ₦500,000'
  },
  {
    id: 'accommodations',
    name: 'Luxury Accommodations',
    description: 'Exclusive villas, penthouses, and boutique hotel access',
    icon: <Home className="w-6 h-6" />,
    features: ['Luxury Properties', '24/7 Concierge', 'Housekeeping', 'Bespoke Amenities'],
    price: 'From ₦300,000'
  },
  {
    id: 'culinary',
    name: 'Culinary Experiences',
    description: 'Private chefs, restaurant reservations, and exclusive tastings',
    icon: <Utensils className="w-6 h-6" />,
    features: ['Private Dining', 'Fine Restaurants', 'Personal Chefs', 'Wine Pairing'],
    price: 'From ₦200,000'
  },
  {
    id: 'events',
    name: 'Private Events',
    description: 'Intimate gatherings, exclusive venue access, VIP event tickets',
    icon: <CalendarDays className="w-6 h-6" />,
    features: ['VIP Access', 'Event Coordination', 'Private Venues', 'Bespoke Catering'],
    price: 'From ₦400,000'
  }
];

const stepLabels = ['Select', 'Options', 'Details', 'Review', 'Done'];

export default function BuildService() {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: '',
    preferredContact: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setSelectedOptions([]);
  };

  const handleOptionToggle = (option: string) => {
    setSelectedOptions(prev =>
      prev.includes(option)
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setContactInfo({ ...contactInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch('/admin/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: contactInfo.name,
          email: contactInfo.email,
          phone: contactInfo.phone,
          city: contactInfo.city,
          service: selectedService?.name ?? '',
          message: [
            selectedOptions.length ? 'Options: ' + selectedOptions.join(', ') : '',
            contactInfo.message,
          ].filter(Boolean).join('\n'),
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setStep(5);
    } catch {
      alert('Something went wrong — please email us directly at info@raha-iduu.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed: Record<number, boolean> = {
    1: selectedService !== null,
    2: selectedOptions.length > 0,
    3: !!(contactInfo.name && contactInfo.email && contactInfo.phone && contactInfo.city),
    4: true,
  };

  return (
    <div className="min-h-screen" style={ { backgroundColor: '#0b1a14' }}>
      {/* Header */}
      <div className="pt-32 pb-12 px-6 border-b border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase mb-4 block">Personalisation</span>
          <h1 className="text-4xl md:text-5xl leading-tight mb-4 text-white" style={ { fontFamily: "'Playfair Display', serif" }}>
            Curate Your Experience
          </h1>
          <p className="text-gray-400 font-light">
            Build a bespoke concierge package tailored entirely to you.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="py-10 px-6 border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between relative">
            {/* Background track */}
            <div className="absolute top-4 left-4 right-4 h-px bg-white/10"></div>
            {/* Active track */}
            <div
              className="absolute top-4 left-4 h-px bg-[#C4A77D] transition-all duration-500"
              style={ { width: `${((step - 1) / (stepLabels.length - 1)) * 100}%` }}
            ></div>

            {stepLabels.map((label, i) => {
              const num = i + 1;
              const isActive = step === num;
              const isDone = step > num;
              return (
                <div key={label} className="relative z-10 flex flex-col items-center gap-2">
                  <div className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                    isDone
                      ? 'border-[#C4A77D] bg-[#C4A77D] text-black'
                      : isActive
                      ? 'border-[#C4A77D] bg-[#0b1a14] text-[#C4A77D]'
                      : 'border-white/20 bg-[#0b1a14] text-gray-600'
                  }`}>
                    {isDone ? <Check size={13} /> : num}
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest ${isActive || isDone ? 'text-[#C4A77D]' : 'text-gray-600'}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">

            {/* Step 1: Service Selection */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                <h2 className="text-2xl text-white mb-8 text-center" style={ { fontFamily: "'Playfair Display', serif" }}>
                  Choose a Service
                </h2>
                <div className="grid md:grid-cols-2 gap-5 mb-10">
                  {services.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => handleServiceSelect(service)}
                      className={`text-left p-7 border transition-all duration-300 group ${
                        selectedService?.id === service.id
                          ? 'border-[#C4A77D] bg-[#C4A77D]/5'
                          : 'border-white/10 bg-white/[0.02] hover:border-[#C4A77D]/40 hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className="flex items-start gap-5">
                        <div className={`w-12 h-12 border flex items-center justify-center flex-shrink-0 transition-colors ${
                          selectedService?.id === service.id ? 'border-[#C4A77D] text-[#C4A77D]' : 'border-white/10 text-[#C4A77D]'
                        }`}>
                          {service.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-lg text-white" style={ { fontFamily: "'Playfair Display', serif" }}>{service.name}</h3>
                            {selectedService?.id === service.id && <Check size={16} className="text-[#C4A77D] flex-shrink-0" />}
                          </div>
                          <p className="text-gray-400 text-sm font-light mb-3">{service.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {service.features.slice(0, 2).map((f) => (
                              <span key={f} className="text-[10px] tracking-widest uppercase text-[#C4A77D] border border-[#C4A77D]/20 px-2 py-0.5">
                                {f}
                              </span>
                            ))}
                          </div>
                          <p className="text-[#C4A77D] text-xs mt-3 font-light">{service.price}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!canProceed[1]}
                    className="flex items-center gap-2 px-8 py-3 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all hover:bg-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Options */}
            {step === 2 && selectedService && (
              <motion.div key="step2" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                <h2 className="text-2xl text-white mb-2 text-center" style={ { fontFamily: "'Playfair Display', serif" }}>
                  Customise Your {selectedService.name}
                </h2>
                <p className="text-gray-500 text-center mb-10 text-sm">Select all that apply</p>
                <div className="grid sm:grid-cols-2 gap-4 mb-10 max-w-2xl mx-auto">
                  {selectedService.features.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleOptionToggle(option)}
                      className={`flex items-center gap-4 p-5 border text-left transition-all duration-300 ${
                        selectedOptions.includes(option)
                          ? 'border-[#C4A77D] bg-[#C4A77D]/5 text-white'
                          : 'border-white/10 text-gray-400 hover:border-[#C4A77D]/30'
                      }`}
                    >
                      <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 transition-colors ${
                        selectedOptions.includes(option) ? 'border-[#C4A77D] bg-[#C4A77D]' : 'border-white/20'
                      }`}>
                        {selectedOptions.includes(option) && <Check size={12} className="text-black" />}
                      </div>
                      <span className="text-sm uppercase tracking-wider">{option}</span>
                    </button>
                  ))}
                </div>
                <div className="flex justify-between max-w-2xl mx-auto">
                  <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors">
                    <ChevronLeft size={16} /> Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!canProceed[2]}
                    className="flex items-center gap-2 px-8 py-3 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all hover:bg-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Contact Details */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                <h2 className="text-2xl text-white mb-2 text-center" style={ { fontFamily: "'Playfair Display', serif" }}>
                  Your Details
                </h2>
                <p className="text-gray-500 text-center mb-10 text-sm">We'll use these to reach you with your bespoke proposal</p>
                <form className="max-w-xl mx-auto space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="text" name="name" placeholder="Full Name" value={contactInfo.name}
                      onChange={handleChange} required
                      className="bg-white/[0.02] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50 w-full"
                    />
                    <input
                      type="email" name="email" placeholder="Email" value={contactInfo.email}
                      onChange={handleChange} required
                      className="bg-white/[0.02] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50 w-full"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="tel" name="phone" placeholder="Phone Number" value={contactInfo.phone}
                      onChange={handleChange} required
                      className="bg-white/[0.02] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50 w-full"
                    />
                    <select
                      name="city" value={contactInfo.city} onChange={handleChange} required
                      className="bg-[#0b1a14] border border-white/10 px-4 py-3 text-gray-400 text-sm focus:outline-none focus:border-[#C4A77D]/50 w-full"
                    >
                      <option value="">Select City</option>
                      <option value="lagos">Lagos</option>
                      <option value="abuja">Abuja</option>
                      <option value="calabar">Calabar</option>
                    </select>
                  </div>
                  <textarea
                    name="message" placeholder="Any additional requirements or notes..." value={contactInfo.message}
                    onChange={handleChange} rows={4}
                    className="bg-white/[0.02] border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#C4A77D]/50 resize-none w-full"
                  />
                </form>
                <div className="flex justify-between max-w-xl mx-auto mt-6">
                  <button onClick={() => setStep(2)} className="flex items-center gap-2 text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors">
                    <ChevronLeft size={16} /> Back
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={!canProceed[3]}
                    className="flex items-center gap-2 px-8 py-3 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all hover:bg-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Review <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Review */}
            {step === 4 && selectedService && (
              <motion.div key="step4" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}>
                <h2 className="text-2xl text-white mb-8 text-center" style={ { fontFamily: "'Playfair Display', serif" }}>
                  Review Your Package
                </h2>
                <div className="max-w-xl mx-auto space-y-4 mb-10">
                  {/* Service */}
                  <div className="border border-white/10 p-6">
                    <p className="text-[10px] uppercase tracking-widest text-[#C4A77D] mb-2">Selected Service</p>
                    <p className="text-white text-lg" style={ { fontFamily: "'Playfair Display', serif" }}>{selectedService.name}</p>
                    <p className="text-gray-400 text-sm">{selectedService.price}</p>
                  </div>
                  {/* Options */}
                  <div className="border border-white/10 p-6">
                    <p className="text-[10px] uppercase tracking-widest text-[#C4A77D] mb-3">Selected Options</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedOptions.map(o => (
                        <span key={o} className="text-xs uppercase tracking-wider text-[#C4A77D] border border-[#C4A77D]/20 px-3 py-1">{o}</span>
                      ))}
                    </div>
                  </div>
                  {/* Contact */}
                  <div className="border border-white/10 p-6">
                    <p className="text-[10px] uppercase tracking-widest text-[#C4A77D] mb-3">Contact Details</p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-400 font-light">
                      <span>Name:</span><span className="text-white">{contactInfo.name}</span>
                      <span>Email:</span><span className="text-white">{contactInfo.email}</span>
                      <span>Phone:</span><span className="text-white">{contactInfo.phone}</span>
                      <span>City:</span><span className="text-white capitalize">{contactInfo.city}</span>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="flex justify-between max-w-xl mx-auto">
                    <button type="button" onClick={() => setStep(3)} className="flex items-center gap-2 text-gray-400 hover:text-white text-xs uppercase tracking-wider transition-colors">
                      <ChevronLeft size={16} /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-8 py-3 border border-[#C4A77D] bg-[#C4A77D] text-black text-xs uppercase tracking-wider transition-all hover:bg-white hover:border-white disabled:opacity-50"
                    >
                      {isSubmitting ? <><Loader className="animate-spin" size={14} /> Sending...</> : <><Send size={14} /> Submit Request</>}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center max-w-lg mx-auto"
              >
                <div className="w-20 h-20 border border-[#C4A77D] rounded-full flex items-center justify-center mx-auto mb-8">
                  <Check className="text-[#C4A77D]" size={36} />
                </div>
                <h2 className="text-3xl text-white mb-4" style={ { fontFamily: "'Playfair Display', serif" }}>Request Submitted</h2>
                <p className="text-gray-400 mb-10 font-light">
                  Your bespoke concierge proposal is being prepared. Our team will reach out within 24 hours to confirm your experience.
                </p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-8 py-3 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all hover:bg-[#C4A77D] hover:text-black"
                >
                  Return Home <ArrowRight size={14} />
                </a>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
